import moment from "moment";
import { DEFAULT_DATE_FORMAT } from "../appRedux/reducers/Common";

/**
 * Reads the company's configured Date Format (Settings → Date/Time
 * Configurations, `common.dateFormat` in Redux - fetched once on app load,
 * see MainApp.js) directly from the store. Falls back to DEFAULT_DATE_FORMAT
 * if it hasn't loaded yet (or failed to load).
 *
 * Plain util functions (table column renders, export filename builders,
 * service helpers, etc.) aren't React components, so they can't use the
 * `useCompanyDateFormat` hook - reading the store singleton directly lets
 * `safeFormatDate` and friends default to the configured format anywhere,
 * while still letting callers pass an explicit format to override it.
 *
 * The store is required lazily here (not imported at module top-level)
 * because this file's other exports (safeLocaleDateString, safeMoment,
 * socialRelativeTime) are plain helpers pulled into many independently
 * code-split route chunks; a top-level `import store from "../appRedux/store"`
 * forced every one of those chunks to also load the full Redux store graph
 * (redux-saga, connected-react-router, ...) before this file finished
 * evaluating, which raced against first-visit chunk loads and could leave
 * unrelated exports temporarily undefined ("Object(...) is not a function").
 * @returns {string}
 */
export const getAppDateFormat = () => {
  try {
    const store = require("../appRedux/store").default;
    return store.getState()?.common?.dateFormat || DEFAULT_DATE_FORMAT;
  } catch (e) {
    return DEFAULT_DATE_FORMAT;
  }
};

/**
 * Safely formats a date value using moment.
 * Returns fallback if date is null, undefined, empty, or produces an invalid moment.
 * @param {*} date - Any date value (string, Date, moment, number)
 * @param {string} format - moment format string (default: the company's configured Date Format)
 * @param {string} fallback - Value returned when date is invalid (default: "-")
 * @returns {string}
 */
export const safeFormatDate = (date, format = getAppDateFormat(), fallback = "-") => {
  if (!date) return fallback;

  const targetFormat = format || "DD-MM-YYYY";

  if (typeof date === "string") {
    const trimmed = date.trim();
    if (!trimmed) return fallback;

    if (/^\d{2}[-/]\d{2}[-/]\d{4}$/.test(trimmed)) {
      const strict = moment(trimmed, ["DD-MM-YYYY", "DD/MM/YYYY", "MM-DD-YYYY", "MM/DD/YYYY"], true);
      if (strict.isValid()) return strict.format(targetFormat);
    }
  }

  const parsed = moment(date);
  return parsed.isValid() ? parsed.format(targetFormat) : fallback;
};

/**
 * Returns a valid moment object, or null if the date is missing or invalid.
 * Use this for Ant Design DatePicker initialValue / value props.
 * @param {*} date
 * @returns {moment.Moment|null}
 */
export const safeMoment = (date) => {
  if (!date) return null;
  const parsed = moment(date);
  return parsed.isValid() ? parsed : null;
};

/**
 * Instagram/Facebook-style relative age label: "now" (<1 min), "Xm" (<1hr),
 * "Xh" (<24hr), "Xd" (<7 days), "Xw" (<~52 weeks), then "Xy". Diffs the
 * stored value against `moment()` — the BROWSER's own current clock, not a
 * server-fetched "now" — so both sides of the diff are always read from the
 * viewer's own clock. `date` is expected to be a real UTC Date value (e.g.
 * Mongoose `created_At`); moment(date) already converts that to the
 * viewer's local time (same rationale as safeFormatDate above), so nothing
 * server-timezone-specific ever enters the comparison — this is the
 * pattern to reach for whenever a "how long ago" label is timezone-risky,
 * not a manual epoch/offset comparison.
 * @param {*} date
 * @returns {string} "" when date is missing/invalid
 */
export const socialRelativeTime = (date) => {
  if (!date) return "";
  const then = moment(date);
  if (!then.isValid()) return "";
  const now = moment();

  const diffMinutes = Math.max(0, now.diff(then, "minutes"));
  if (diffMinutes < 1) return "now";
  if (diffMinutes < 60) return `${diffMinutes}m`;

  const diffHours = now.diff(then, "hours");
  if (diffHours < 24) return `${diffHours}h`;

  const diffDays = now.diff(then, "days");
  if (diffDays < 7) return `${diffDays}d`;

  const diffWeeks = now.diff(then, "weeks");
  if (diffWeeks < 52) return `${diffWeeks}w`;

  return `${now.diff(then, "years")}y`;
};

/**
 * Safely formats a date using native toLocaleDateString.
 * Returns fallback if date is null, undefined, or invalid.
 * @param {*} date
 * @param {string} locale
 * @param {object} options - Intl.DateTimeFormatOptions
 * @param {string} fallback
 * @returns {string}
 */
export const safeLocaleDateString = (date, locale, options, fallback = "-") => {
  if (!date) return fallback;
  const d = new Date(date);
  if (isNaN(d.getTime())) return fallback;
  return d.toLocaleDateString(locale, options);
};
